import React, { useEffect, useState } from "react";
import {
    Button,
    Dropdown,
    Header,
    Icon,
    Label,
    Loader,
    Message,
    Modal,
    Pagination,
    Segment,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { getDetailedOrderReport } from "../../../services/reporting.service";
import {
    DetailedOrder,
    DetailedOrderReportResponse,
    ReportFilters,
    SortDirection,
} from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
    riskColor,
    riskLabel,
} from "../reportingFormatters";
import OrderReportDetailModal from "./OrderReportDetailModal";

export interface DashboardOrdersDrilldown {
    key: string;
    title: string;
    description: string;
    icon: any;
    filters: ReportFilters;
    status?: string;
    summary: Array<{ label: string; value: string; status?: string }>;
}

interface Props {
    drilldown?: DashboardOrdersDrilldown;
    onClose: () => void;
}

const DashboardOrdersModal = ({ drilldown, onClose }: Props) => {
    const [data, setData] = useState<DetailedOrderReportResponse>();
    const [selectedOrder, setSelectedOrder] = useState<DetailedOrder>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [sortProperty, setSortProperty] = useState("outstandingBalance");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    useEffect(() => {
        if (!drilldown) {
            setData(undefined);
            return;
        }
        let mounted = true;
        const load = async () => {
            setLoading(true);
            setError(undefined);
            try {
                const response = await getDetailedOrderReport(drilldown.filters, {
                    page,
                    size,
                    sort: `${sortProperty},${sortDirection}`,
                });
                if (mounted) setData(response.data);
            } catch (requestError: any) {
                const message = requestError.message || "No fue posible consultar los pedidos de esta fila.";
                if (mounted) {
                    setError(message);
                    toast.error(message);
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [drilldown, page, size, sortProperty, sortDirection]);

    const changeSort = (property: string) => {
        setPage(0);
        setSortDirection((current) => sortProperty === property && current === "asc" ? "desc" : "asc");
        setSortProperty(property);
    };
    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;
    const openOrder = (order: DetailedOrder) => setSelectedOrder(order);

    return (
        <>
            <Modal
                open={Boolean(drilldown)}
                onClose={onClose}
                size="fullscreen"
                closeIcon
                closeOnDimmerClick
                className="dashboard-fullscreen-modal dashboard-row-modal"
            >
                <Modal.Header className="dashboard-product-modal__header">
                    <Header as="h3">
                        <Icon name={drilldown?.icon || "list alternate outline"} />
                        <Header.Content>
                            {drilldown?.title || "Detalle de la fila"}
                            {drilldown?.status && (
                                <Label color={orderStatusColor(drilldown.status)}>
                                    {orderStatusLabel(drilldown.status)}
                                </Label>
                            )}
                            <Header.Subheader>Pedidos que componen este resultado</Header.Subheader>
                        </Header.Content>
                    </Header>
                </Modal.Header>
                <Modal.Content scrolling className="dashboard-product-modal__content">
                    {drilldown && (
                        <Message className="dashboard-product-view-message" icon>
                            <Icon name="info circle" />
                            <Message.Content>
                                <Message.Header>Alcance del desglose</Message.Header>
                                {drilldown.description}
                            </Message.Content>
                        </Message>
                    )}

                    {drilldown && (
                        <div className="dashboard-row-modal__summary">
                            {drilldown.summary.map((item) => (
                                <Segment key={item.label}>
                                    <span>{item.label}</span>
                                    <strong>
                                        {item.status ? (
                                            <Label color={orderStatusColor(item.status)}>
                                                {orderStatusLabel(item.status, item.value)}
                                            </Label>
                                        ) : item.value}
                                    </strong>
                                </Segment>
                            ))}
                        </div>
                    )}

                    {error && <Message negative icon="warning circle" header="No fue posible cargar el detalle" content={error} />}
                    {loading && (
                        <Segment basic placeholder className="dashboard-product-modal__loading">
                            <Loader active>Consultando réplica...</Loader>
                        </Segment>
                    )}
                    {!loading && data?.orders.length === 0 && (
                        <Message info icon="info circle" header="Sin pedidos" content="No hay pedidos que coincidan con esta fila y los filtros actuales." />
                    )}
                    {!loading && Boolean(data?.orders.length) && (
                        <Segment className="dashboard-product-orders">
                            <div className="dashboard-product-orders__heading">
                                <Header as="h4"><Icon name="list alternate outline" /> Pedidos incluidos</Header>
                                <Label basic>{formatNumber(data?.totalElements)} pedidos</Label>
                            </div>
                            <div className="dashboard-table-scroll">
                                <Table sortable striped compact selectable className="dashboard-table dashboard-report-table dashboard-clickable-table">
                                    <Table.Header><Table.Row>
                                        <Table.HeaderCell sorted={sorted("riskScore")} onClick={() => changeSort("riskScore")}>Riesgo</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("invoice")} onClick={() => changeSort("invoice")}>Factura</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("createdAt")} onClick={() => changeSort("createdAt")}>Creación</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("currentStatus")} onClick={() => changeSort("currentStatus")}>Estado</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("clientName")} onClick={() => changeSort("clientName")}>Cliente</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("productName")} onClick={() => changeSort("productName")}>Producto</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("advisorName")} onClick={() => changeSort("advisorName")}>Asesor</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("totalAmount")} onClick={() => changeSort("totalAmount")} textAlign="right">Total</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("paidAmount")} onClick={() => changeSort("paidAmount")} textAlign="right">Pagado</Table.HeaderCell>
                                        <Table.HeaderCell sorted={sorted("outstandingBalance")} onClick={() => changeSort("outstandingBalance")} textAlign="right">Saldo</Table.HeaderCell>
                                        <Table.HeaderCell textAlign="right">Cobranza</Table.HeaderCell>
                                        <Table.HeaderCell />
                                    </Table.Row></Table.Header>
                                    <Table.Body>{data?.orders.map((order) => (
                                        <Table.Row
                                            key={order.id}
                                            warning={order.management.requiresAttention}
                                            onClick={() => openOrder(order)}
                                            tabIndex={0}
                                            onKeyDown={(event: React.KeyboardEvent) => {
                                                if (event.key === "Enter" || event.key === " ") openOrder(order);
                                            }}
                                        >
                                            <Table.Cell><Label color={riskColor(order.management.riskLevel)}>{riskLabel(order.management.riskLevel)}</Label></Table.Cell>
                                            <Table.Cell><strong>#{order.invoice || "—"}</strong></Table.Cell>
                                            <Table.Cell>{formatReportDate(order.createdAt)}</Table.Cell>
                                            <Table.Cell>
                                                <Label color={orderStatusColor(order.currentStatus?.code)}>
                                                    {orderStatusLabel(order.currentStatus?.code, order.currentStatus?.label)}
                                                </Label>
                                            </Table.Cell>
                                            <Table.Cell>{order.client?.fullName || "Sin cliente"}</Table.Cell>
                                            <Table.Cell><strong>{order.product?.name || "Sin producto"}</strong><small>{order.product?.sku || "Sin SKU"}</small></Table.Cell>
                                            <Table.Cell>{order.advisor?.fullName || "Sin asignar"}</Table.Cell>
                                            <Table.Cell textAlign="right">{formatCurrency(order.totalAmount)}</Table.Cell>
                                            <Table.Cell textAlign="right">{formatCurrency(order.paidAmount)}</Table.Cell>
                                            <Table.Cell textAlign="right"><strong>{formatCurrency(order.outstandingBalance)}</strong></Table.Cell>
                                            <Table.Cell textAlign="right">{formatPercent(order.management.paymentProgressPercent)}</Table.Cell>
                                            <Table.Cell collapsing><Icon name="chevron right" color="grey" /></Table.Cell>
                                        </Table.Row>
                                    ))}</Table.Body>
                                </Table>
                            </div>
                        </Segment>
                    )}

                    {data && data.totalElements > 0 && (
                        <div className="dashboard-pagination dashboard-pagination--split">
                            <span>{formatNumber(data.totalElements)} pedidos</span>
                            <Pagination
                                activePage={page + 1}
                                totalPages={Math.max(data.totalPages, 1)}
                                onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                            />
                            <Dropdown
                                compact
                                selection
                                value={size}
                                options={[10, 25, 50].map((value) => ({ key: value, value, text: `${value} filas` }))}
                                onChange={(_, value) => { setPage(0); setSize(Number(value.value)); }}
                            />
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions><Button basic onClick={onClose}><Icon name="cancel" /> Cerrar</Button></Modal.Actions>
            </Modal>
            <OrderReportDetailModal order={selectedOrder} onClose={() => setSelectedOrder(undefined)} />
        </>
    );
};

export default DashboardOrdersModal;
